import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import React, { useMemo, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiMoreVertical,
} from 'react-icons/fi';

// Type casting for icons
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMoreVerticalComponent =
  FiMoreVertical as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

// Interface for automation row data
interface AutomationRow {
  id: string;
  name: string;
  recipients: string;
  openRate: string;
  clickRate: string;
  type: string;
  status: string;
  sentDate: string;
  triggerDate: string;
  action: string;
}

interface ReportsViewAutomationTableProps {
  automations: AutomationRow[];
}

// Function to determine status color
// const getStatusColor = (status: string) => {
//   switch (status) {
//     case 'Active':
//       return 'bg-green-100 text-green-600';
//     case 'Draft':
//       return 'bg-red-100 text-red-600';
//     case 'Schedule':
//       return 'bg-yellow-100 text-yellow-600';
//     default:
//       return 'bg-gray-100 text-gray-600';
//   }
// };

// Component to display a table of automation campaigns
const ReportsViewAutomationTable: React.FC<ReportsViewAutomationTableProps> = ({
  automations,
}) => {
  // const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AutomationRow;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  const itemsPerPage = 6;

  // Sorting logic with useMemo for performance
  const sortedAutomations = useMemo(() => {
    if (!sortConfig) return automations;
    const sorted = [...automations].sort((a, b) => {
      const valA = a[sortConfig.key]?.toString().toLowerCase() ?? '';
      const valB = b[sortConfig.key]?.toString().toLowerCase() ?? '';
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [automations, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedAutomations.length / itemsPerPage);
  const paginatedAutomations = sortedAutomations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle sorting
  const requestSort = (key: keyof AutomationRow) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key: keyof AutomationRow) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? (
        <FiChevronUpComponent size={14} />
      ) : (
        <FiChevronDownComponent size={14} />
      );
    }
    return <FiChevronDownComponent size={14} />;
  };

  // Checkbox selection logic
  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(paginatedAutomations.map((c) => c.id));
    } else {
      setSelected([]);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  // Export to CSV
  const exportCSV = () => {
    const rows = automations.filter((c) => selected.includes(c.id));
    const exportableFields: (keyof AutomationRow)[] = [
      'id',
      'name',
      'type',
      'status',
      'recipients',
      'openRate',
      'clickRate',
      'triggerDate',
    ];
    const header = exportableFields.join(',');
    const body = rows
      .map((r) => exportableFields.map((field) => r[field]).join(','))
      .join('\n');
    const csvContent = [header, body].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'automations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to PDF
  const exportPDF = () => {
    const rows = automations.filter((c) => selected.includes(c.id));
    const exportableFields: (keyof AutomationRow)[] = [
      'id',
      'name',
      'type',
      'status',
      'recipients',
      'openRate',
      'clickRate',
      'triggerDate',
    ];
    const doc = new jsPDF();
    autoTable(doc, {
      head: [exportableFields],
      body: rows.map((r) => exportableFields.map((field) => r[field])),
    });
    doc.save('automations.pdf');
  };

  return (
    <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm'>
      {/* Header with Export Button */}
      <div className='flex justify-between items-center px-6 pt-4'>
        <h2 className='text-[16px] font-semibold text-[#3F5265]'>Campaigns</h2>
        <div className='relative'>
          <button
            onClick={() => setExportOpen((prev) => !prev)}
            disabled={selected.length === 0}
            className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center text-white ${selected.length === 0
              ? 'bg-gray-200 cursor-not-allowed'
              : 'bg-orange-500'
              }`}
          >
            Export <FiChevronDownComponent size={16} className='ml-1' />
          </button>
          {exportOpen && selected.length > 0 && (
            <div className='absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10'>
              <button
                onClick={() => {
                  exportPDF();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as PDF
              </button>
              <button
                onClick={() => {
                  exportCSV();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className='px-6 mt-3'>
        <table className='w-full text-left border border-gray-200'>
          <thead className='bg-gray-100'>
            <tr className='text-gray-600 text-sm border-b'>
              <th className='px-6 py-3'>
                <input
                  type='checkbox'
                  onChange={toggleSelectAll}
                  checked={
                    selected.length === paginatedAutomations.length &&
                    paginatedAutomations.length > 0
                  }
                  className='w-4 h-4 rounded'
                />
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('name')}
              >
                <div className='flex items-center gap-1'>
                  CAMPAIGN NAME {getSortIcon('name')}
                </div>
              </th>
              <th className='px-6 py-3 font-normal'>RECIPIENTS</th>
              <th className='px-6 py-3 font-normal'>OPEN RATE</th>
              <th className='px-6 py-3 font-normal'>CLICK RATE</th>
              <th className='px-6 py-3 font-normal justify-end flex'>
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className='text-[#3F5265] text-sm'>
            {paginatedAutomations.map((c) => (
              <tr key={c.id} className='border-b hover:bg-gray-50'>
                <td className='px-6 py-4'>
                  <input
                    type='checkbox'
                    checked={selected.includes(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className='w-4 h-4 rounded'
                  />
                </td>
                <td className='px-6 py-4'>{c.name}</td>
                <td className='px-6 py-4'>{c.recipients}</td>
                <td className='px-6 py-4'>{c.openRate}</td>
                <td className='px-6 py-4'>{c.clickRate}</td>
                <td className='px-6 py-4 flex items-center space-x-3 justify-end'>
                  <button className='text-teal-600 flex items-center gap-1 hover:underline'>
                    <FiEyeComponent /> View
                  </button>
                  <FiMoreVerticalComponent className='text-gray-400 cursor-pointer' />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500'>
        <span>
          Showing {paginatedAutomations.length} results out of{' '}
          {automations.length}
        </span>
        {totalPages > 1 && (
          <div className='flex items-center gap-2'>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className={`px-3 py-1 border rounded ${currentPage === 1
                ? 'bg-gray-200 text-gray-300'
                : 'bg-orange-500 text-white'
                }`}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${currentPage === i + 1
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600'
                  }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className={`px-3 py-1 border rounded ${currentPage === totalPages
                ? 'bg-gray-200 text-gray-300'
                : 'bg-orange-500 text-white'
                }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsViewAutomationTable;
